import { useEffect, useRef, useState } from 'react';
import { RECEIPT_UPLOAD_ACCEPT } from '@pet/shared';
import type {
  CreatePaymentBody,
  OwnerOption,
  PaymentSource,
  PossibleDuplicate,
  PropertyOption,
  SpendingPurpose,
  TaxCategoryOption,
} from '@pet/shared';
import {
  uploadReceipt,
  getIngestion,
  getCategories,
  getProperties,
  type IngestionStatusResponse,
} from '../lib/receiptClient';
import {
  getOwners,
  createPayment,
  getPossibleDuplicates,
  ReceiptApiError,
  type CreatePaymentResponse,
} from '../lib/paymentClient';
import { buildPaymentPayload, type PaymentDraft } from '../lib/buildPaymentPayload';
import {
  allocate,
  composeMemo,
  fiftyFiftyPreset,
  parsePercent,
  BLOCK_REASON_TEXT,
  type AllocationResult,
} from '../lib/allocation';
import { parseDollarsToCents, formatCentsAsDollars } from '../lib/money';
import {
  allocateItems,
  buildReceiptItemAllocation,
  emptyItemDraft,
  itemBlockReasonTexts,
  itemsDescription,
  receiptItemsGateOpen,
  type ItemDraft,
  type ItemShare,
} from '../lib/receiptItems';
import { isPreService } from '../lib/serviceStatus';
import type { FixDraft, FixLine } from '../lib/fixDraft';
import {
  ACCOUNT_LABELS,
  DESCRIPTION_REQUIRED_REASON,
  METHOD_OPTIONS,
  ChipGroup,
  DollarInput,
  HelpButton,
  NavRow,
  PurposeControls,
  SmallPurchaseControls,
  categoriesFor,
  mapPostError,
  shortPropertyLabel,
  todayISO,
  type SpendLine,
} from './spending/controls';
import AssignScreen, { type SplitMode } from './spending/AssignScreen';
import LineEditor from './spending/LineEditor';
import GuideSection from './help/GuideSection';
import { GUIDE_SECTIONS } from './help/guideContent';
import SummaryScreen from './spending/SummaryScreen';
import DuplicateWarningScreen from './spending/DuplicateWarningScreen';
import styles from './AddSpending.module.css';

// AD-18: the one spending entry point. Every save is one POST /api/payments. A chosen document
// is uploaded and read first, and travels to the save as its ingestion id — never as a file part.
// Nothing is written except the upload and the final save.

// Advisory only; it blocks nothing (AD-07 — gating is an open question).
const LOW_CONFIDENCE_THRESHOLD = 0.8;
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60_000;

interface AddSpendingProps {
  onDone: () => void;
  // "Fix this payment" (AD-19): the deleted payment's answers, and the receipt ingestion its delete
  // reset (null when it had none). Absent for every ordinary entry.
  fix?: { draft: FixDraft; resetIngestionId: string | null };
}

// Fix's notes on the first step (release-checkpoint.md F3, F6).
const FIX_NOTE = 'The old payment is deleted. Save at the end to enter it again.';
const RECEIPT_LOST_NOTE = "This receipt couldn't be reopened, so this entry will have no receipt attached.";

// "?" help: the live Guide sections for the two treatment questions, shown verbatim (tax-data.md:
// no new tax explanation). A title that is not found leaves that "?" out; the tests require both.
const PURPOSE_HELP = GUIDE_SECTIONS.findIndex((s) => s.title === '"What was this for?"');
const SMALL_PURCHASE_HELP = GUIDE_SECTIONS.findIndex((s) => s.title === 'The small-purchase question');

let lineKeyCounter = 0;
function nextLineKey(): string {
  lineKeyCounter += 1;
  return `line-${Date.now()}-${lineKeyCounter}`;
}

// A Fix line as the wizard holds it. The description counts as typed, so nothing derived replaces
// it; the de minimis flag is the stored one, not the category default.
function spendLineFrom(line: FixLine, withAmount: boolean): SpendLine {
  return {
    id: nextLineKey(),
    propertyId: line.propertyId,
    purpose: line.purpose,
    taxCategoryKey: line.taxCategoryKey,
    amountCents: null,
    description: line.description,
    deMinimisEligible: line.deMinimisEligible,
    amountText: withAmount ? formatCentsAsDollars(line.amountCents) : '',
    percentText: '',
    descriptionEdited: true,
  };
}

type Document =
  | { kind: 'none' }
  | { kind: 'receipt'; ingestionId: string; extraction: IngestionStatusResponse };

type Screen =
  | { name: 'document' }
  | { name: 'uploading' }
  | { name: 'upload-error'; message: string }
  | { name: 'reading'; ingestionId: string }
  | { name: 'reopening'; ingestionId: string }
  | { name: 'still-reading'; ingestionId: string }
  | { name: 'payee' }
  | { name: 'date' }
  | { name: 'total' }
  | { name: 'account' }
  | { name: 'method' }
  | { name: 'where' }
  | { name: 'purpose' }
  | { name: 'category' }
  | { name: 'small-purchase' }
  | { name: 'description' }
  | { name: 'assign' }
  | { name: 'line'; key: string }
  | { name: 'summary' }
  | { name: 'checking' }
  | { name: 'duplicate-warning'; matches: PossibleDuplicate[]; body: CreatePaymentBody; propertyTotal: number }
  | { name: 'saving' }
  | { name: 'saved'; response: CreatePaymentResponse; totalCents: number }
  | { name: 'error'; message: string }
  | { name: 'already-saved' };

export default function AddSpending({ onDone, fix }: AddSpendingProps) {
  const fixDraft = fix?.draft;
  const fixSplit = fixDraft?.where === 'split';
  const [screen, setScreen] = useState<Screen>(() =>
    !fix ? { name: 'document' } : fix.resetIngestionId ? { name: 'reopening', ingestionId: fix.resetIngestionId } : { name: 'payee' }
  );
  // A Fix entry, until startOver() begins a plain one ("A draft never carries over", ux-wizard.md).
  const [fixActive, setFixActive] = useState(fix !== undefined);
  // The old payment's receipt cannot come back: its ingestion is not confirmable, or there was none
  // to reopen although a receipt was attached.
  const [receiptLost, setReceiptLost] = useState(
    fix !== undefined && fix.resetIngestionId === null && fix.draft.hadReceipt
  );
  const [categories, setCategories] = useState<TaxCategoryOption[]>([]);
  const [properties, setProperties] = useState<PropertyOption[]>([]);
  const [owners, setOwners] = useState<OwnerOption[]>([]);

  // The draft. Everything below is reset by startOver().
  const [doc, setDoc] = useState<Document>({ kind: 'none' });
  const [lowConfidence, setLowConfidence] = useState(false);
  const [suggestedCategory, setSuggestedCategory] = useState('');
  // A Fix prefills every answer except funding source and owner (AD-19), which start unanswered.
  const [payee, setPayee] = useState(fixDraft?.payee ?? '');
  const [date, setDate] = useState(fixDraft?.date ?? '');
  const [totalText, setTotalText] = useState(fixDraft?.totalText ?? '');
  const [paymentSource, setPaymentSource] = useState<PaymentSource | ''>('');
  const [ownerId, setOwnerId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(fixDraft?.paymentMethod ?? '');
  const [where, setWhere] = useState(fixDraft?.where ?? ''); // a property id, 'split', or '' (unanswered)
  const [oneLine, setOneLine] = useState<SpendLine>(() =>
    fixDraft && !fixSplit ? spendLineFrom(fixDraft.lines[0], false) : emptyLine('')
  );
  const [splitMode, setSplitMode] = useState<SplitMode>('amounts');
  const [lines, setLines] = useState<SpendLine[]>(() =>
    fixDraft && fixSplit ? fixDraft.lines.map((l) => spendLineFrom(l, true)) : []
  );
  // Build C: where each extracted receipt item was tapped, aligned with the extraction's items.
  const [itemDrafts, setItemDrafts] = useState<ItemDraft[]>([]);
  const [personalText, setPersonalText] = useState(fixDraft?.personalText ?? '');
  const [personalPercentText, setPersonalPercentText] = useState('');
  const [salesTaxText, setSalesTaxText] = useState('');
  // The Guide section open as "?" help, or null. It replaces the step on screen; `screen` and the
  // draft are untouched, so closing it returns to the same step with its answers.
  const [helpSection, setHelpSection] = useState<number | null>(null);
  const openHelp = (i: number) => (i >= 0 ? () => setHelpSection(i) : undefined);

  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    getCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
    getProperties()
      .then(setProperties)
      .catch(() => setProperties([]));
    getOwners()
      .then(setOwners)
      .catch(() => setOwners([]));
  }, []);

  // Poll the ingestion until the server resolves it. The wizard never moves past this while the
  // row is PENDING_AI: after POLL_TIMEOUT_MS it parks on "Still reading" instead.
  useEffect(() => {
    if (screen.name !== 'reading') return;
    const ingestionId = screen.ingestionId;
    const startedAt = Date.now();
    let stopped = false;

    async function tick() {
      let result: IngestionStatusResponse;
      try {
        result = await getIngestion(ingestionId);
      } catch {
        // A network hiccup counts as "still reading"; the next tick tries again.
        result = { id: ingestionId, status: 'PENDING_AI', mime: '', createdAt: '' };
      }
      if (stopped) return;
      if (result.status === 'AI_COMPLETE' || result.status === 'AI_FAILED') {
        stop();
        applyExtraction(ingestionId, result);
        return;
      }
      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        stop();
        setScreen({ name: 'still-reading', ingestionId });
      }
    }

    function stop() {
      stopped = true;
      if (pollTimer.current) clearInterval(pollTimer.current);
    }

    pollTimer.current = setInterval(tick, POLL_INTERVAL_MS);
    void tick();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen.name === 'reading' ? screen.ingestionId : null]);

  // Fix: reopen the reset ingestion with one read, never the poll. A confirmable one becomes the
  // receipt document and the old answers win over its extraction (F2); any other answer, or a
  // failed read, continues with no document and says so (F3).
  useEffect(() => {
    if (screen.name !== 'reopening' || !fixDraft) return;
    const ingestionId = screen.ingestionId;
    let stopped = false;
    getIngestion(ingestionId)
      .then((result) => {
        if (stopped) return;
        if (result.status === 'AI_COMPLETE' || result.status === 'AI_FAILED') {
          applyExtraction(ingestionId, result);
          setPayee(fixDraft.payee);
          setDate(fixDraft.date);
          setTotalText(fixDraft.totalText);
        } else {
          setReceiptLost(true);
          setScreen({ name: 'payee' });
        }
      })
      .catch(() => {
        if (stopped) return;
        setReceiptLost(true);
        setScreen({ name: 'payee' });
      });
    return () => {
      stopped = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen.name === 'reopening' ? screen.ingestionId : null]);

  // Nothing is answered on a new line. What it was for is asked before its category (AD-10); the
  // AI's category is offered only after an answer, and only within that answer's group.
  function emptyLine(propertyId: string): SpendLine {
    return {
      id: nextLineKey(),
      propertyId,
      purpose: '',
      taxCategoryKey: '',
      amountCents: null,
      description: '',
      deMinimisEligible: null,
      amountText: '',
      percentText: '',
      descriptionEdited: false,
    };
  }

  function startOver() {
    setFixActive(false);
    setReceiptLost(false);
    setDoc({ kind: 'none' });
    setLowConfidence(false);
    setSuggestedCategory('');
    setPayee('');
    setDate('');
    setTotalText('');
    setPaymentSource('');
    setOwnerId('');
    setPaymentMethod('');
    setWhere('');
    setOneLine(emptyLine(''));
    setSplitMode('amounts');
    setLines([]);
    setItemDrafts([]);
    setPersonalText('');
    setPersonalPercentText('');
    setSalesTaxText('');
  }

  function applyExtraction(ingestionId: string, result: IngestionStatusResponse) {
    setDoc({ kind: 'receipt', ingestionId, extraction: result });
    setLowConfidence(
      result.status === 'AI_FAILED' ||
        result.confidence === undefined ||
        result.confidence < LOW_CONFIDENCE_THRESHOLD
    );
    if (result.status === 'AI_COMPLETE') {
      setPayee(result.vendor ?? '');
      setDate(result.date ?? '');
      setTotalText(result.totalCents === undefined ? '' : formatCentsAsDollars(result.totalCents));
      setSuggestedCategory(result.taxCategoryKey ?? '');
      setItemDrafts((result.lineItems ?? []).map(() => emptyItemDraft()));
    }
    setScreen({ name: 'payee' });
  }

  async function handleFileSelected(file: File) {
    startOver();
    setScreen({ name: 'uploading' });
    try {
      const ingestion = await uploadReceipt(file);
      setScreen({ name: 'reading', ingestionId: ingestion.id });
    } catch (err) {
      setScreen({
        name: 'upload-error',
        message:
          err instanceof ReceiptApiError
            ? err.message
            : "That upload didn't go through. Please try again.",
      });
    }
  }

  function chooseNoDocument() {
    startOver();
    setDate(todayISO());
    setScreen({ name: 'payee' });
  }

  function chooseAccount(value: PaymentSource) {
    // On a Fix, the first tap of the old account keeps the prefilled method (it belongs to that
    // account); any other change clears it as before.
    const keepFixMethod = fixActive && paymentSource === '' && value === fixDraft?.oldSource;
    if (value !== paymentSource && !keepFixMethod) {
      // A method chosen for one account may not exist for the other.
      setPaymentMethod('');
      if (value === 'LLC_FUNDED') setOwnerId('');
    }
    setPaymentSource(value);
  }

  function selectCategory(line: SpendLine, key: string): SpendLine {
    const cat = categories.find((c) => c.key === key);
    // Every category choice resets the small-purchase flag to that category's default (AD-10
    // prefill); the human can then override it.
    return {
      ...line,
      taxCategoryKey: key,
      deMinimisEligible: cat ? cat.deMinimisEligibleDefault : null,
    };
  }

  // Answering "What was this for?" sets the category: Improvement is capital_improvement alone;
  // another answer takes the AI's category only if it is in that answer's group, else none.
  function choosePurpose(line: SpendLine, purpose: SpendingPurpose): SpendLine {
    if (purpose === line.purpose) return line;
    const offered = categoriesFor(categories, purpose);
    const key =
      purpose === 'improvement'
        ? (offered[0]?.key ?? '')
        : (offered.find((c) => c.key === suggestedCategory)?.key ?? '');
    return { ...selectCategory(line, key), purpose };
  }

  // AD-10: a line whose property is pre-service on the payment date. Computed, never stored.
  function linePreService(line: SpendLine): boolean {
    const property = properties.find((p) => p.id === line.propertyId);
    return property !== undefined && isPreService(property.placedInServiceDate, date);
  }

  // The pre-service default: when the purpose question is first shown for a line with a
  // pre-service property and no answer, Improvement is pre-selected — over the AI's suggestion.
  // Once applied it is an ordinary answer.
  function withPreServiceDefault(line: SpendLine): SpendLine {
    return line.purpose === '' && linePreService(line) ? choosePurpose(line, 'improvement') : line;
  }

  // Mirrors the server (AD-10 D2): a pre-service line in a non-capital category needs a
  // description.
  function descriptionRequired(line: SpendLine): boolean {
    const cat = categories.find((c) => c.key === line.taxCategoryKey);
    return cat !== undefined && !cat.isCapitalDefault && linePreService(line);
  }

  function needsDescription(line: SpendLine): boolean {
    return descriptionRequired(line) && line.description.trim() === '';
  }

  function updateLine(key: string, change: (l: SpendLine) => SpendLine) {
    setLines((ls) => ls.map((l) => (l.id === key ? change(l) : l)));
  }

  function removeLine(key: string) {
    setLines((ls) => ls.filter((l) => l.id !== key));
  }

  function addLineAndEdit() {
    const line = emptyLine('');
    setLines((ls) => [...ls, line]);
    setScreen({ name: 'line', key: line.id });
  }

  function applyFiftyFifty() {
    const preset = fiftyFiftyPreset(properties);
    if (!preset) return;
    setSplitMode('percentages');
    setLines(preset.map((p) => ({ ...emptyLine(p.propertyId), percentText: String(p.percent) })));
    setPersonalPercentText('');
  }

  // ---- derived values ----
  const totalCents = parseDollarsToCents(totalText);
  const totalValid = totalCents !== null && totalCents > 0;
  const isSplit = where === 'split';
  const addressOf = (propertyId: string) =>
    properties.find((p) => p.id === propertyId)?.address ?? '';
  const labelOf = (propertyId: string) => {
    const address = addressOf(propertyId);
    return address ? shortPropertyLabel(address) : '—';
  };
  const categoryLabel = (key: string) => categories.find((c) => c.key === key)?.label ?? '—';
  const owner = owners.find((o) => o.id === ownerId);

  // Build C. The gate (E3) is the extraction's own; Tap items is also offered only while the
  // typed total is the extracted one, since the items prove only the extracted total. When the
  // human closes it by going back, the screen reverts to typed amounts and no mapping is sent.
  const extraction = doc.kind === 'receipt' ? doc.extraction : null;
  const receiptItems = extraction?.lineItems ?? [];
  const itemsGate = extraction !== null && receiptItemsGateOpen(extraction);
  const tapItemsOffered = itemsGate && totalCents === extraction.totalCents;
  const effectiveSplitMode: SplitMode = splitMode === 'items' && !tapItemsOffered ? 'amounts' : splitMode;
  const itemsMode = isSplit && effectiveSplitMode === 'items';
  const extractedTaxCents = extraction?.taxCents ?? 0;
  const itemAllocation = itemsMode
    ? allocateItems({
        items: receiptItems,
        drafts: itemDrafts,
        lines: lines.map((l) => ({ id: l.id, propertyAddress: addressOf(l.propertyId) })),
      })
    : null;

  // A description nobody has typed is derived, never stored: a split line's tapped items (items
  // mode only), the one-property line's items (gate open), otherwise blank as before Build C.
  const withDescription = (line: SpendLine, prefill: string): SpendLine =>
    line.descriptionEdited ? line : { ...line, description: prefill };
  const shownLines = lines.map((l) =>
    withDescription(l, itemAllocation ? itemsDescription(receiptItems, itemAllocation.shares, l.id) : '')
  );
  const shownOneLine = withDescription(oneLine, itemsGate ? itemsDescription(receiptItems, null, null) : '');
  const allocatedLines: SpendLine[] = isSplit ? shownLines : [{ ...shownOneLine, propertyId: where }];

  function computeAllocation(): AllocationResult {
    const total = totalCents ?? 0;
    if (!isSplit) {
      return allocate({
        mode: 'amounts',
        totalCents: total,
        lines: [{ id: oneLine.id, propertyAddress: addressOf(where), amountCents: total }],
        personalCents: null,
        salesTaxText: '',
      });
    }
    if (itemAllocation) {
      // E5: the items' before-tax cents are the typed amounts; the extracted tax is the sales tax.
      return allocate({
        mode: 'amounts',
        totalCents: total,
        lines: lines.map((l) => ({
          id: l.id,
          propertyAddress: addressOf(l.propertyId),
          amountCents: itemAllocation.lineCents[l.id],
        })),
        personalCents: itemAllocation.personalCents,
        salesTaxText: formatCentsAsDollars(extractedTaxCents),
      });
    }
    if (effectiveSplitMode === 'percentages') {
      return allocate({
        mode: 'percentages',
        totalCents: total,
        lines: lines.map((l) => ({
          id: l.id,
          propertyAddress: addressOf(l.propertyId),
          percent: parsePercent(l.percentText),
        })),
        personalPercent: parsePercent(personalPercentText),
      });
    }
    return allocate({
      mode: 'amounts',
      totalCents: total,
      lines: lines.map((l) => ({
        id: l.id,
        propertyAddress: addressOf(l.propertyId),
        amountCents: parseDollarsToCents(l.amountText),
      })),
      personalCents: parseDollarsToCents(personalText),
      salesTaxText,
    });
  }

  const allocation = computeAllocation();
  const incomplete = allocatedLines.some((l) => !l.propertyId || !l.taxCategoryKey);
  // While items are still unassigned, the totals' "not assigned" reasons describe those items and
  // the item reasons say it in their words instead.
  const itemsPending =
    itemAllocation !== null &&
    (itemAllocation.unassigned.length > 0 || itemAllocation.incompleteSplits.length > 0);
  const allocationBlocked = itemsPending
    ? allocation.blocked.filter(
        (r) => r !== 'under-assigned' && r !== 'over-assigned' && r !== 'nothing-before-tax'
      )
    : allocation.blocked;
  const blockReasons = [
    ...(itemAllocation ? itemBlockReasonTexts(itemAllocation) : []),
    ...allocationBlocked.map((r) => BLOCK_REASON_TEXT[r]),
    ...(incomplete ? ['Choose a property and a category for every line.'] : []),
    // Defensive: every step that writes a description is gated on it; this is reachable only by
    // going Back and changing the date or the property afterwards.
    ...allocatedLines.flatMap((l, i) => (needsDescription(l) ? [`Line ${i + 1} needs a description.`] : [])),
  ];
  const personalCents = allocation.personal.writtenCents;
  const salesTaxEntered =
    (isSplit && effectiveSplitMode === 'amounts' && (parseDollarsToCents(salesTaxText) ?? 0) > 0) ||
    (itemsMode && extractedTaxCents > 0);
  const fiftyFifty = fiftyFiftyPreset(properties);

  async function handleSave() {
    if (blockReasons.length > 0 || totalCents === null) return;
    const writtenOf = (key: string) =>
      allocation.lines.find((l) => l.id === key)?.writtenCents ?? 0;
    const draft: PaymentDraft = {
      date,
      payee,
      paymentMethod,
      paymentSource,
      ownerId,
      lines: allocatedLines.map((l) => ({ ...l, amountCents: writtenOf(l.id) })),
    };
    if (personalCents > 0) draft.memo = composeMemo(personalCents, totalCents);
    if (doc.kind === 'receipt') {
      draft.receiptIngestionId = doc.ingestionId;
      draft.receiptTotalCents = totalCents;
      // The mapping is sent only when the lines came from tapped items (E7).
      if (itemAllocation && itemAllocation.shares.every((s) => s !== null)) {
        draft.receiptItemAllocation = buildReceiptItemAllocation(
          receiptItems,
          itemAllocation.shares as ItemShare[][],
          allocatedLines.map((l) => l.id)
        );
      }
    }
    const result = buildPaymentPayload(draft, categories);
    if (!result.ok) {
      setScreen({ name: 'error', message: result.error });
      return;
    }
    const propertyTotal = draft.lines.reduce((s, l) => s + (l.amountCents ?? 0), 0);
    // Disables Confirm & Save before the first await, so a second tap cannot start a second save.
    setScreen({ name: 'checking' });
    let matches: PossibleDuplicate[] = [];
    try {
      matches = await getPossibleDuplicates({
        date,
        payee,
        totalCents: propertyTotal,
        ingestionId: doc.kind === 'receipt' ? doc.ingestionId : undefined,
      });
    } catch {
      // The warning is advisory (AD-20): a failed check never stops the save.
      matches = [];
    }
    if (matches.length > 0) {
      setScreen({ name: 'duplicate-warning', matches, body: result.body, propertyTotal });
      return;
    }
    await save(result.body, propertyTotal);
  }

  async function save(body: CreatePaymentBody, propertyTotal: number) {
    setScreen({ name: 'saving' });
    try {
      // No second argument: the wizard never sends a file part.
      const response = await createPayment(body);
      setScreen({ name: 'saved', response, totalCents: propertyTotal });
    } catch (err) {
      if (err instanceof ReceiptApiError && err.status === 409) {
        setScreen({ name: 'already-saved' });
        return;
      }
      setScreen({ name: 'error', message: mapPostError(err) });
    }
  }

  const banner = lowConfidence && doc.kind === 'receipt' && (
    <p className={styles.banner} role="alert">
      Please double-check — we weren't sure about this one.
    </p>
  );

  if (helpSection !== null) {
    return (
      <GuideSection
        section={GUIDE_SECTIONS[helpSection]}
        backLabel="Back to the question"
        onBack={() => setHelpSection(null)}
      />
    );
  }

  // ---- 1. document ----
  if (screen.name === 'document') {
    return (
      <main className={styles.container}>
        <header className={styles.header}>
          <h1 className={styles.title}>Add Spending</h1>
          <p className={styles.subtitle}>Do you have a receipt or a bill?</p>
        </header>
        <label className={styles.primaryButton}>
          Take photo
          <input
            type="file"
            accept={RECEIPT_UPLOAD_ACCEPT}
            capture="environment"
            className={styles.hiddenFileInput}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFileSelected(file);
            }}
          />
        </label>
        <label className={styles.secondaryButton}>
          Choose file
          <input
            type="file"
            accept={RECEIPT_UPLOAD_ACCEPT}
            className={styles.hiddenFileInput}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFileSelected(file);
            }}
          />
        </label>
        <button className={styles.secondaryButton} onClick={chooseNoDocument}>
          No document
        </button>
        <button className={styles.secondaryButton} onClick={onDone}>
          Back
        </button>
      </main>
    );
  }

  if (screen.name === 'uploading') {
    return (
      <main className={styles.container} aria-busy="true">
        <p className={styles.statusText}>Uploading…</p>
      </main>
    );
  }

  if (screen.name === 'upload-error') {
    return (
      <main className={styles.container}>
        <p className={styles.banner} role="alert">
          {screen.message}
        </p>
        <button className={styles.primaryButton} onClick={() => setScreen({ name: 'document' })}>
          Try again
        </button>
        <button className={styles.secondaryButton} onClick={onDone}>
          Back
        </button>
      </main>
    );
  }

  if (screen.name === 'reading') {
    return (
      <main className={styles.container} aria-busy="true">
        <p className={styles.statusText}>Reading your receipt…</p>
      </main>
    );
  }

  if (screen.name === 'reopening') {
    return (
      <main className={styles.container} aria-busy="true">
        <p className={styles.statusText}>Opening the receipt…</p>
      </main>
    );
  }

  if (screen.name === 'still-reading') {
    const ingestionId = screen.ingestionId;
    return (
      <main className={styles.container}>
        <p className={styles.statusText}>Still reading your receipt…</p>
        <p className={styles.subtitle}>This is taking longer than usual.</p>
        <button
          className={styles.primaryButton}
          onClick={() => setScreen({ name: 'reading', ingestionId })}
        >
          Keep waiting
        </button>
        <button className={styles.secondaryButton} onClick={() => setScreen({ name: 'document' })}>
          Back
        </button>
      </main>
    );
  }

  // ---- 2. who was paid ----
  if (screen.name === 'payee') {
    return (
      <main className={styles.container}>
        {banner}
        {fixActive && receiptLost && <p className={styles.overrideNote}>{RECEIPT_LOST_NOTE}</p>}
        {fixActive && <p className={styles.overrideNote}>{FIX_NOTE}</p>}
        <h2 className={styles.question}>Who was paid?</h2>
        <input
          className={styles.textInput}
          type="text"
          placeholder="Store, company or person"
          value={payee}
          onChange={(e) => setPayee(e.target.value)}
          aria-label="Who was paid"
        />
        <NavRow
          onBack={() => setScreen({ name: 'document' })}
          onNext={() => setScreen({ name: 'date' })}
          canNext={payee.trim().length > 0}
        />
      </main>
    );
  }

  // ---- 3. when ----
  if (screen.name === 'date') {
    return (
      <main className={styles.container}>
        {banner}
        <h2 className={styles.question}>When was it paid?</h2>
        <input
          className={styles.textInput}
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          aria-label="Date paid"
        />
        <NavRow
          onBack={() => setScreen({ name: 'payee' })}
          onNext={() => setScreen({ name: 'total' })}
          canNext={date !== ''}
        />
      </main>
    );
  }

  // ---- 4. total paid ----
  if (screen.name === 'total') {
    return (
      <main className={styles.container}>
        {banner}
        <h2 className={styles.question}>How much was paid in total?</h2>
        <DollarInput text={totalText} onText={setTotalText} ariaLabel="Total paid in dollars" />
        <NavRow
          onBack={() => setScreen({ name: 'date' })}
          onNext={() => setScreen({ name: 'account' })}
          canNext={totalValid}
        />
      </main>
    );
  }

  // ---- 5a. which account ----
  if (screen.name === 'account') {
    const canNext =
      paymentSource === 'LLC_FUNDED' || (paymentSource === 'OWNER_FUNDED' && ownerId !== '');
    return (
      <main className={styles.container}>
        <h2 className={styles.question}>Where did the money come from?</h2>
        <ChipGroup
          ariaLabel="Where the money came from"
          options={[
            { value: 'LLC_FUNDED', label: ACCOUNT_LABELS.LLC_FUNDED },
            { value: 'OWNER_FUNDED', label: ACCOUNT_LABELS.OWNER_FUNDED },
          ]}
          selected={paymentSource}
          onSelect={chooseAccount}
        />
        {paymentSource === 'OWNER_FUNDED' && (
          <>
            <h2 className={styles.question}>Which owner?</h2>
            <ChipGroup
              ariaLabel="Which owner"
              options={owners.map((o) => ({ value: o.id, label: o.name }))}
              selected={ownerId}
              onSelect={setOwnerId}
            />
          </>
        )}
        <NavRow
          onBack={() => setScreen({ name: 'total' })}
          onNext={() => setScreen({ name: 'method' })}
          canNext={canNext}
        />
      </main>
    );
  }

  // ---- 5b. how it was paid (optional) ----
  if (screen.name === 'method') {
    const options = paymentSource ? METHOD_OPTIONS[paymentSource] : [];
    return (
      <main className={styles.container}>
        <h2 className={styles.question}>How was it paid?</h2>
        <p className={styles.overrideNote}>Optional — skip it if none of these fit.</p>
        <ChipGroup
          ariaLabel="How it was paid"
          options={options.map((m) => ({ value: m, label: m }))}
          selected={paymentMethod}
          onSelect={(m) => setPaymentMethod((current) => (current === m ? '' : m))}
        />
        <NavRow
          onBack={() => setScreen({ name: 'account' })}
          onNext={() => setScreen({ name: 'where' })}
          canNext
          nextLabel={paymentMethod ? 'Next' : 'Skip'}
        />
      </main>
    );
  }

  // ---- 6. one property, or split ----
  if (screen.name === 'where') {
    return (
      <main className={styles.container}>
        {banner}
        <h2 className={styles.question}>Is everything for one property?</h2>
        <ChipGroup
          ariaLabel="Which property"
          options={[
            ...properties.map((p) => ({ value: p.id, label: shortPropertyLabel(p.address) })),
            { value: 'split', label: 'Split it' },
          ]}
          selected={where}
          onSelect={setWhere}
        />
        <NavRow
          onBack={() => setScreen({ name: 'method' })}
          onNext={() => {
            if (where === 'split') {
              if (lines.length === 0) {
                setLines([emptyLine('')]);
                // Build C (E6): Tap items is the default when the receipt's items can be tapped.
                if (tapItemsOffered) setSplitMode('items');
              }
              setScreen({ name: 'assign' });
            } else {
              // The purpose question is shown now: a pre-service property pre-selects
              // Improvement if nothing is answered yet (AD-10).
              setOneLine((l) => withPreServiceDefault({ ...l, propertyId: where }));
              setScreen({ name: 'purpose' });
            }
          }}
          canNext={where !== ''}
        />
      </main>
    );
  }

  // The one-property line as the save will see it (with its derived description, Build C).
  const oneLineHere: SpendLine = { ...shownOneLine, propertyId: where };

  if (screen.name === 'purpose') {
    return (
      <main className={styles.container}>
        {banner}
        {PURPOSE_HELP >= 0 ? (
          <div className={styles.questionRow}>
            <h2 className={styles.question}>What was this for?</h2>
            <HelpButton question="What was this for?" onClick={() => setHelpSection(PURPOSE_HELP)} />
          </div>
        ) : (
          <h2 className={styles.question}>What was this for?</h2>
        )}
        <PurposeControls
          line={oneLine}
          preService={linePreService(oneLineHere)}
          onSelect={(purpose) => setOneLine((l) => choosePurpose(l, purpose))}
        />
        <NavRow
          onBack={() => setScreen({ name: 'where' })}
          onNext={() =>
            setScreen(
              oneLine.purpose === 'improvement' ? { name: 'small-purchase' } : { name: 'category' }
            )
          }
          canNext={oneLine.purpose !== ''}
        />
      </main>
    );
  }

  if (screen.name === 'category') {
    return (
      <main className={styles.container}>
        {banner}
        <h2 className={styles.question}>What kind of expense is this?</h2>
        <ChipGroup
          ariaLabel="Tax category"
          options={categoriesFor(categories, oneLine.purpose).map((c) => ({ value: c.key, label: c.label }))}
          selected={oneLine.taxCategoryKey}
          onSelect={(key) => setOneLine((l) => selectCategory(l, key))}
        />
        <NavRow
          onBack={() => setScreen({ name: 'purpose' })}
          onNext={() => setScreen({ name: 'small-purchase' })}
          canNext={oneLine.taxCategoryKey !== ''}
        />
      </main>
    );
  }

  if (screen.name === 'small-purchase') {
    return (
      <main className={styles.container}>
        {banner}
        <h2 className={styles.question}>Small-purchase treatment</h2>
        <SmallPurchaseControls
          line={oneLine}
          categories={categories}
          onChange={setOneLine}
          onHelp={openHelp(SMALL_PURCHASE_HELP)}
        />
        <NavRow
          onBack={() =>
            setScreen(oneLine.purpose === 'improvement' ? { name: 'purpose' } : { name: 'category' })
          }
          onNext={() => setScreen({ name: 'description' })}
          canNext={oneLine.deMinimisEligible !== null}
        />
      </main>
    );
  }

  if (screen.name === 'description') {
    const required = descriptionRequired(oneLineHere);
    return (
      <main className={styles.container}>
        {banner}
        <h2 className={styles.question}>Describe it</h2>
        <p className={styles.overrideNote}>{required ? DESCRIPTION_REQUIRED_REASON : 'Optional.'}</p>
        <input
          className={styles.textInput}
          type="text"
          placeholder="e.g. paint and brushes for the kitchen"
          value={oneLineHere.description}
          onChange={(e) =>
            setOneLine((l) => ({ ...l, description: e.target.value, descriptionEdited: true }))
          }
          aria-label="Description"
        />
        <NavRow
          onBack={() => setScreen({ name: 'small-purchase' })}
          onNext={() => setScreen({ name: 'summary' })}
          canNext={!needsDescription(oneLineHere)}
        />
      </main>
    );
  }

  // ---- 6 (split). a line's details, then the assignment screen ----
  if (screen.name === 'line') {
    const line = shownLines.find((l) => l.id === screen.key);
    if (line) {
      return (
        <LineEditor
          banner={banner}
          line={line}
          properties={properties}
          categories={categories}
          preService={linePreService(line)}
          descriptionRequired={descriptionRequired(line)}
          onChange={(next) => updateLine(line.id, () => next)}
          onSelectProperty={(l, propertyId) => withPreServiceDefault({ ...l, propertyId })}
          onSelectPurpose={choosePurpose}
          onSelectCategory={selectCategory}
          onRemove={() => {
            removeLine(line.id);
            setScreen({ name: 'assign' });
          }}
          onDone={() => setScreen({ name: 'assign' })}
          onHelpPurpose={openHelp(PURPOSE_HELP)}
          onHelpSmallPurchase={openHelp(SMALL_PURCHASE_HELP)}
        />
      );
    }
    // A line that no longer exists falls through to the assignment screen.
  }

  if (screen.name === 'assign' || screen.name === 'line') {
    return (
      <AssignScreen
        banner={banner}
        totalCents={totalCents ?? 0}
        splitMode={effectiveSplitMode}
        onSplitMode={setSplitMode}
        itemsOffered={tapItemsOffered}
        items={receiptItems}
        itemDrafts={itemDrafts}
        onItemDraft={(index, next) =>
          setItemDrafts((drafts) => drafts.map((d, i) => (i === index ? next : d)))
        }
        itemAllocation={itemAllocation}
        taxCents={extractedTaxCents}
        fiftyFiftyLabel={
          fiftyFifty
            ? `50/50 between ${labelOf(fiftyFifty[0].propertyId)} and ${labelOf(fiftyFifty[1].propertyId)}`
            : null
        }
        onFiftyFifty={applyFiftyFifty}
        lines={lines}
        onLineChange={updateLine}
        onLineDetails={(key) => {
          // A line that already has a property (the 50/50 preset) gets the pre-service default
          // when its details are first shown (AD-10).
          updateLine(key, withPreServiceDefault);
          setScreen({ name: 'line', key });
        }}
        onLineRemove={removeLine}
        onAddLine={addLineAndEdit}
        labelOf={labelOf}
        categoryLabel={categoryLabel}
        personalText={personalText}
        onPersonalText={setPersonalText}
        personalPercentText={personalPercentText}
        onPersonalPercentText={setPersonalPercentText}
        salesTaxText={salesTaxText}
        onSalesTaxText={setSalesTaxText}
        salesTaxEntered={salesTaxEntered}
        allocation={allocation}
        blockReasons={blockReasons}
        onBack={() => setScreen({ name: 'where' })}
        onNext={() => setScreen({ name: 'summary' })}
      />
    );
  }

  // ---- 7. summary ----
  if (screen.name === 'duplicate-warning') {
    const pending = screen;
    return (
      <DuplicateWarningScreen
        matches={pending.matches}
        onBack={() => setScreen({ name: 'summary' })}
        onSaveAnyway={() => void save(pending.body, pending.propertyTotal)}
      />
    );
  }

  if (screen.name === 'summary' || screen.name === 'checking' || screen.name === 'saving') {
    return (
      <SummaryScreen
        banner={banner}
        payee={payee}
        date={date}
        paymentSource={paymentSource}
        ownerName={owner?.name ?? null}
        paymentMethod={paymentMethod}
        totalCents={totalCents}
        lines={allocatedLines}
        allocation={allocation}
        salesTaxEntered={salesTaxEntered}
        categories={categories}
        labelOf={labelOf}
        categoryLabel={categoryLabel}
        blockReasons={blockReasons}
        isSaving={screen.name === 'checking' || screen.name === 'saving'}
        onBack={() => setScreen(isSplit ? { name: 'assign' } : { name: 'description' })}
        onSave={handleSave}
      />
    );
  }

  // ---- error (draft intact; back to review) ----
  if (screen.name === 'error') {
    return (
      <main className={styles.container}>
        <p className={styles.banner} role="alert">
          {screen.message}
        </p>
        <button className={styles.primaryButton} onClick={() => setScreen({ name: 'summary' })}>
          Back to review
        </button>
        <button className={styles.secondaryButton} onClick={onDone}>
          Home
        </button>
      </main>
    );
  }

  if (screen.name === 'already-saved') {
    return (
      <main className={styles.container}>
        <p className={styles.statusText}>This receipt has already been saved.</p>
        <button className={styles.primaryButton} onClick={onDone}>
          Home
        </button>
      </main>
    );
  }

  if (screen.name === 'saved') {
    return (
      <main className={styles.container}>
        <p className={styles.savedText}>Saved ✓</p>
        <div className={styles.summaryList}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Lines saved</span>
            <span className={styles.summaryValue}>{screen.response.transactionIds.length}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>For the properties</span>
            <span className={styles.summaryValue}>${formatCentsAsDollars(screen.totalCents)}</span>
          </div>
        </div>
        <button
          className={styles.primaryButton}
          onClick={() => {
            startOver();
            setScreen({ name: 'document' });
          }}
        >
          Add another
        </button>
        <button className={styles.secondaryButton} onClick={onDone}>
          Done
        </button>
      </main>
    );
  }

  return null;
}
